// CC03/CC06 · Panel "Completar datos" en la ficha.
// Ejecuta el motor de completitud: auto-aplica los deterministas (y los muestra
// en auditoría) y lista las propuestas para aceptar / corregir / rechazar.
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Wand2, Check, Pencil, X } from "lucide-react";
import { API } from "@/context/AuthContext";

function ProposalRow({ p, onResolve }) {
  const [mode, setMode] = useState(null); // "rechazar" | "corregir"
  const [val, setVal] = useState(p.valor_propuesto);
  const [motivo, setMotivo] = useState("");
  return (
    <div className="bg-white rounded border border-amber-100 px-2 py-1.5 text-[11px]" data-testid={`completitud-prop-${p.campo}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-semibold text-gray-700">{p.label}</span>
        <span className="px-1.5 py-0.5 rounded bg-sky-100 text-sky-800 font-semibold">→ {p.valor_propuesto}</span>
        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${p.confianza === "derivada" ? "bg-sky-100 text-sky-700" : "bg-amber-100 text-amber-700"}`}>{p.confianza}</span>
        <span className="text-gray-400 italic truncate max-w-[220px]">{p.fuente}</span>
        {!mode && (
          <span className="ml-auto flex gap-1">
            <button onClick={() => onResolve(p.id, "aceptar")} title="Aceptar" data-testid={`completitud-accept-${p.campo}`} className="text-emerald-700 hover:bg-emerald-50 rounded p-0.5"><Check size={13}/></button>
            <button onClick={() => { setMode("corregir"); setVal(p.valor_propuesto); }} title="Corregir" data-testid={`completitud-correct-${p.campo}`} className="text-sky-700 hover:bg-sky-50 rounded p-0.5"><Pencil size={12}/></button>
            <button onClick={() => setMode("rechazar")} title="Rechazar" data-testid={`completitud-reject-${p.campo}`} className="text-rose-700 hover:bg-rose-50 rounded p-0.5"><X size={13}/></button>
          </span>
        )}
      </div>
      {mode === "corregir" && (
        <div className="flex items-center gap-1 mt-1">
          <input value={val} onChange={e => setVal(e.target.value)} className="border rounded px-1 py-0.5 text-[11px] flex-1" data-testid={`completitud-correct-input-${p.campo}`}/>
          <button onClick={() => onResolve(p.id, "corregir", { valor_corregido: val })} className="text-[10px] font-semibold text-sky-700">Guardar</button>
          <button onClick={() => setMode(null)} className="text-[10px] text-gray-400">Cancelar</button>
        </div>
      )}
      {mode === "rechazar" && (
        <div className="flex items-center gap-1 mt-1">
          <input value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Motivo del rechazo" className="border rounded px-1 py-0.5 text-[11px] flex-1" data-testid={`completitud-reject-input-${p.campo}`}/>
          <button onClick={() => onResolve(p.id, "rechazar", { motivo: motivo || "sin motivo" })} className="text-[10px] font-semibold text-rose-700">Rechazar</button>
          <button onClick={() => setMode(null)} className="text-[10px] text-gray-400">Cancelar</button>
        </div>
      )}
    </div>
  );
}

export default function CompletitudPanel({ oid }) {
  const [proposals, setProposals] = useState([]);
  const [audit, setAudit] = useState([]);
  const [running, setRunning] = useState(false);
  const [ran, setRan] = useState(false);

  const load = useCallback(() => {
    if (!oid) return;
    axios.get(`${API}/ofertas-comercial/${oid}/completitud/proposals`)
      .then(r => { setProposals(r.data.proposals || []); setAudit(r.data.audit || []); }).catch(() => {});
  }, [oid]);
  useEffect(() => { load(); }, [load]);

  const run = async () => {
    setRunning(true);
    try { await axios.post(`${API}/ofertas-comercial/${oid}/completitud/run`); setRan(true); load(); }
    finally { setRunning(false); }
  };
  const resolve = async (pid, accion, extra = {}) => {
    await axios.post(`${API}/ofertas-comercial/completitud/proposals/${pid}/resolve`, { accion, ...extra });
    load();
  };

  if (!oid) return null;
  const pending = proposals.filter(p => p.estado === "pendiente");

  return (
    <div className="border-b border-gray-100 px-4 py-2 bg-amber-50/40" data-testid="completitud-panel">
      <div className="flex items-center gap-2">
        <Wand2 size={13} className="text-amber-600"/>
        <span className="text-[11px] font-semibold text-gray-700">Completar datos</span>
        {audit.length > 0 && <span className="text-[10px] text-emerald-700">· {audit.length} auto-aplicado(s)</span>}
        {pending.length > 0 && <span className="text-[10px] text-amber-700">· {pending.length} propuesta(s)</span>}
        <button onClick={run} disabled={running} data-testid="completitud-run-btn"
          className="ml-auto text-[10px] font-semibold px-2 py-0.5 rounded bg-[#007934] text-white disabled:opacity-50">
          {running ? "Buscando…" : "Buscar datos que faltan"}
        </button>
      </div>

      {pending.length > 0 && (
        <div className="mt-2 space-y-1" data-testid="completitud-pending">
          {pending.map(p => <ProposalRow key={p.id} p={p} onResolve={resolve}/>)}
        </div>
      )}
      {ran && pending.length === 0 && (
        <p className="mt-1 text-[10px] text-gray-500">No hay propuestas pendientes. Los datos deterministas se aplicaron automáticamente (ver auditoría).</p>
      )}
      {audit.length > 0 && (
        <div className="mt-2" data-testid="completitud-audit">
          <div className="text-[9px] uppercase font-semibold text-gray-400 mb-0.5">Auditoría de completado</div>
          {audit.slice(-5).map((a, i) => (
            <div key={i} className="text-[10px] text-gray-500">
              <b className="text-emerald-700">{a.campo}</b> = {a.valor} {a.auto ? "(auto)" : "(confirmado)"} · {a.fuente}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
